// ============================================
// SEO.jsx — Componente reutilizable para meta tags por página.
// Usa react-helmet-async para inyectar title/description/canonical/OG
// y opcionalmente datos estructurados JSON-LD.
// ============================================

import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.physiologyskincare.com";
const SITE_NAME = "Physiology Skincare Co";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-cover.jpg`;

/**
 * @param {object} p
 * @param {string} p.title           Título único de la página (sin sufijo de marca).
 * @param {string} p.description     Descripción para search snippet y previews sociales.
 * @param {string} [p.path]          Ruta canonical sin host, ej "/shop".
 * @param {string} [p.image]         URL absoluta para og:image y twitter:image.
 * @param {"website"|"product"|"article"} [p.type="website"]
 * @param {boolean} [p.noindex]      Si true, agrega meta robots noindex.
 * @param {object|object[]} [p.jsonLd] Objeto(s) JSON-LD ya armados para inyectar.
 */
export default function SEO({
  title,
  description,
  path = "",
  image,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  const url = path ? `${SITE_URL}${path}` : SITE_URL;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_MX" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {ldArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

// ============================================
// Helpers para construir JSON-LD por tipo de página
// ============================================

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
}

/**
 * Producto con offers condicional — solo incluimos el bloque `offers` si hay
 * un precio mayor a 0. Esto evita errores de "structured data" en Search
 * Console mientras no haya precios reales en Medusa.
 */
export function productJsonLd({
  title,
  description,
  image,
  brand = SITE_NAME,
  sku,
  handle,
  price,
  currency = "MXN",
  availability = "https://schema.org/InStock",
}) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    description,
    brand: { "@type": "Brand", name: brand },
    image: Array.isArray(image) ? image : image ? [image] : undefined,
  };
  if (sku) data.sku = sku;
  if (handle) data.url = `${SITE_URL}/products/${handle}`;

  const validPrice = typeof price === "number" && price > 0;
  if (validPrice) {
    data.offers = {
      "@type": "Offer",
      price: price.toFixed(2),
      priceCurrency: currency,
      availability,
      url: data.url,
    };
  }
  return data;
}

export function articleJsonLd({ title, description, image, slug, datePublished, author }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image: image ? [image] : undefined,
    datePublished,
    author: author ? { "@type": "Person", name: author } : undefined,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-phys.svg` },
    },
    mainEntityOfPage: `${SITE_URL}/journal/${slug}`,
  };
}

export { SITE_URL, SITE_NAME };
